import { useEffect, useRef } from 'react';
import { useNoteTabs } from '@/contexts/NoteTabsContext';

interface UseNoteTabProps {
  uuid: string | undefined;
  noteTitle?: string;
  isLoaded: boolean;
  hasError: boolean;
}

/**
 * Custom hook to manage note tab lifecycle
 * - Adds note to tabs when loaded
 * - Sets as active tab
 * - Removes tab if note not found
 * - Deactivates tab on unmount
 * - Exposes removeTab for manual removal (e.g., when deleting a note)
 * Note: Tab title updates are handled by useManageNote when saving
 */
export const useNoteTab = ({ uuid, noteTitle, isLoaded, hasError }: UseNoteTabProps) => {
  const { addTab, setActiveTab, removeTab } = useNoteTabs();
  const isTabAddedRef = useRef(false);

  // Add note to tabs and set as active when note is loaded (only once)
  useEffect(() => {
    if (isLoaded && uuid && !isTabAddedRef.current) {
      addTab(uuid, noteTitle || 'Untitled');
      setActiveTab(uuid);
      isTabAddedRef.current = true;
    }
  }, [isLoaded, uuid, noteTitle, addTab, setActiveTab]);

  // Remove tab if note not found or error occurred
  useEffect(() => {
    if (hasError && uuid) {
      removeTab(uuid);
      isTabAddedRef.current = false;
    }
  }, [hasError, uuid, removeTab]);

  // Cleanup: deactivate tab when leaving the page
  useEffect(() => {
    return () => {
      if (uuid) {
        setActiveTab('');
      }
    };
  }, [uuid, setActiveTab]);

  return { removeTab };
};
